import { ConsoleLogger } from 'amazon-chime-sdk-js';
import type { Logger, LogLevel } from 'amazon-chime-sdk-js';

import { resolveChimeLogLevel } from './chimeLogLevel';

/**
 * Known Chime SDK teardown / SDK-bug messages that are expected during
 * intentional hang-up and should not surface as console errors.
 *
 * - MeetingEnded / SignalingChannelClosedUnexpectedly: normal stop() races
 * - session will not be reconnected: terminal status during leave
 * - Unhandled type ... flattening attributes: SDK bug — signalingDropped
 *   includes boolean `signalingCloseWasClean` which flattenEventAttributes
 *   does not accept (amazon-chime-sdk-js 3.30.0)
 */
const BENIGN_TEARDOWN_ERROR_PATTERNS: RegExp[] = [
  /session will not be reconnected/i,
  /status code MeetingEnded/i,
  /meeting already ended/i,
  /status code SignalingChannelClosedUnexpectedly/i,
  /Unhandled type received while flattening attributes/i,
  /Error reporting event/i,
];

/**
 * Messages that race ahead of (or fire without) our tearing-down flag —
 * e.g. backend ends the meeting first. Always downgrade these.
 */
const ALWAYS_DOWNGRADE_ERROR_PATTERNS: RegExp[] = [
  /session will not be reconnected/i,
  /status code MeetingEnded/i,
  /meeting already ended/i,
  /Unhandled type received while flattening attributes/i,
];

export const isBenignChimeTeardownError = (message: string): boolean =>
  BENIGN_TEARDOWN_ERROR_PATTERNS.some((pattern) => pattern.test(message));

export const shouldAlwaysDowngradeChimeError = (message: string): boolean =>
  ALWAYS_DOWNGRADE_ERROR_PATTERNS.some((pattern) => pattern.test(message));

type TeardownGate = {
  isTearingDown: () => boolean;
};

/**
 * ConsoleLogger wrapper that downgrades expected teardown / SDK-bug errors
 * while a meeting session is being intentionally stopped.
 */
export class FilteredChimeLogger implements Logger {
  private readonly inner: ConsoleLogger;
  private readonly teardownGate: TeardownGate;

  constructor(
    teardownGate: TeardownGate,
    level: LogLevel = resolveChimeLogLevel()
  ) {
    this.inner = new ConsoleLogger('Chime', level);
    this.teardownGate = teardownGate;
  }

  info(msg: string): void {
    this.inner.info(msg);
  }

  warn(msg: string): void {
    this.inner.warn(msg);
  }

  error(msg: string): void {
    if (shouldAlwaysDowngradeChimeError(msg)) {
      this.inner.warn(msg);
      return;
    }

    if (this.teardownGate.isTearingDown() && isBenignChimeTeardownError(msg)) {
      // Downgrade to warn so expected hang-up noise is not red ERROR spam.
      this.inner.warn(msg);
      return;
    }

    this.inner.error(msg);
  }

  debug(debugFunction: string | (() => string)): void {
    this.inner.debug(debugFunction);
  }

  setLogLevel(level: LogLevel): void {
    this.inner.setLogLevel(level);
  }

  getLogLevel(): LogLevel {
    return this.inner.getLogLevel();
  }
}
